import React, { useState, useEffect, useCallback } from "react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import { Percent, Plus, Pencil, Trash2, Save, X, Loader2, Building2 } from "lucide-react";

const inp = "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400 bg-white";
const btn = "px-4 py-2 rounded-xl text-sm font-semibold transition inline-flex items-center gap-2";

const PLAN_EMPTY = { name: "", description: "", commissionPercent: "" };

export default function MembershipPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'create' | plan
  const [form, setForm] = useState(PLAN_EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get("/memberships/plans", { params: { includeInactive: true } });
      setPlans(res.data?.data?.plans ?? []);
    } catch { toast.error("Error cargando los planes"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(PLAN_EMPTY); setModal("create"); };
  const openEdit = (p) => {
    setForm({ name: p.name, description: p.description || "", commissionPercent: String(p.commissionPercent) });
    setModal(p);
  };
  const closeModal = () => { setModal(null); setForm(PLAN_EMPTY); };

  const handleSave = async () => {
    const commissionPercent = Number(form.commissionPercent);
    if (!form.name.trim()) { toast.warn("El nombre es requerido"); return; }
    if (isNaN(commissionPercent) || commissionPercent < 0 || commissionPercent > 100) {
      toast.warn("El % de comisión debe estar entre 0 y 100");
      return;
    }
    setSaving(true);
    try {
      if (modal === "create") {
        await API.post("/memberships/plans", { ...form, commissionPercent });
        toast.success("Plan creado");
      } else {
        await API.patch(`/memberships/plans/${modal.id}`, { ...form, commissionPercent });
        toast.success("Plan actualizado");
      }
      await load();
      closeModal();
    } catch (err) { toast.error(err.response?.data?.message || "Error"); }
    finally { setSaving(false); }
  };

  const handleToggleActive = async (p) => {
    try {
      await API.patch(`/memberships/plans/${p.id}`, { active: !p.active });
      await load();
    } catch { toast.error("Error al cambiar estado"); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Percent className="text-supernova-600" size={26} /> Planes de membresía
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Definen el % de comisión que Qkiere cobra por pedido a cada tienda. Se asignan desde el detalle de cada empresa.
          </p>
        </div>
        <button onClick={openCreate} className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600`}>
          <Plus size={14} /> Nuevo plan
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 size={28} className="animate-spin text-supernova-500" /></div>
      ) : plans.length === 0 ? (
        <div className="text-center py-14 text-gray-400">
          <Percent size={36} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">Sin planes todavía</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {plans.map((p) => (
            <div key={p.id} className={`bg-white border rounded-2xl p-4 shadow-sm ${p.active ? "border-gray-200" : "border-gray-100 opacity-60"}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="font-bold text-gray-800 text-sm">{p.name}</p>
                <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-semibold ${p.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                  {p.active ? "Activo" : "Inactivo"}
                </span>
              </div>
              <p className="text-2xl font-bold text-supernova-600 mb-1">{Number(p.commissionPercent)}%</p>
              {p.description && <p className="text-xs text-gray-500 mb-2">{p.description}</p>}
              <p className="text-xs text-gray-400 flex items-center gap-1 mb-3">
                <Building2 size={11} /> {p._count?.companies ?? 0} tienda(s) en este plan
              </p>
              <div className="flex gap-2">
                <button onClick={() => openEdit(p)} className={`${btn} text-xs bg-gray-50 text-gray-600 hover:bg-gray-100`}>
                  <Pencil size={11} /> Editar
                </button>
                <button onClick={() => handleToggleActive(p)} className={`${btn} text-xs border ${p.active ? "border-gray-200 text-gray-600 hover:bg-gray-50" : "border-green-200 text-green-700 hover:bg-green-50"}`}>
                  {p.active ? <Trash2 size={11} /> : <Plus size={11} />} {p.active ? "Desactivar" : "Reactivar"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={closeModal}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800">{modal === "create" ? "Nuevo plan" : "Editar plan"}</h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1 block">Nombre *</label>
                <input className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Básico, Pro, Premium..." />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1 block">% de comisión *</label>
                <input type="number" step="0.01" min="0" max="100" className={inp} value={form.commissionPercent}
                  onChange={(e) => setForm({ ...form, commissionPercent: e.target.value })} placeholder="10" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1 block">Descripción</label>
                <textarea className={inp} rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Beneficios de este plan (opcional)" />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={closeModal} className={`${btn} border border-gray-200 text-gray-600 hover:bg-gray-50`}>Cancelar</button>
              <button onClick={handleSave} disabled={saving} className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600 disabled:opacity-60`}>
                <Save size={14} /> {saving ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
